import { DEV } from 'cc/env';
import { _decorator, CCInteger, Gradient, Texture2D, Sprite, UITransform, CCBoolean, ParticleSystem, v4 } from 'cc';
import BaseShaderManipulator from './BaseShaderManipulator';
import ITintObject, { Tint } from './TintObject';

const { ccclass, property, executeInEditMode } = _decorator;

@ccclass('TintParticle')
@executeInEditMode
export default class TintParticle extends BaseShaderManipulator implements ITintObject {
    @property(Sprite)
    private _testSampleColor: Sprite = null;

    @property(Texture2D)
    private _mainTexture: Texture2D = null;

    @property(CCBoolean)
    private _useOriginColor: boolean = true;

    @property(CCBoolean)
    private _useColorOverLifetimeModule: boolean = false;

    @property(CCBoolean)
    private _premultipliedAlpha: boolean = false;

    @property
    private _grayValueOffset: number = 0;

    @property(CCInteger)
    private _textureWidth: number = 100;

    @property(Gradient)
    private _tintGradient: Gradient = new Gradient();

    @property
    private _useSampleBound: boolean = false;

    @property
    private _sampleLeftBound: number = 0;

    @property
    private _sampleRightBound: number = 1;

    private _texture: Texture2D = new Texture2D();

    @property({ type: CCInteger, min: 1, max: 10000, displayName: DEV && 'Tint 裁切大小' })
    public get textureWidth(): number {
        return this._textureWidth;
    }
    public set textureWidth(value: number) {
        if (this._textureWidth === value || value < 1 || value > 10000) {
            return;
        }
        this._textureWidth = value;
        this._updateGradientProperty();
    }

    @property({
        type: CCBoolean,
        displayName: DEV && '採用 Color Module 顏色',
        tooltip:
            DEV &&
            `是否採用 Particle 上 ColorOverLifetimeModule 顏色，不包含 Alpha
            *會受到 Start Color 影響，建議把 Start Color 改為白色
            *若和採用原圖顏色同時啟用，會乘上第二次ColorModule的顏色`,
    })
    public get useColorOverLifetimeModule(): boolean {
        return this._useColorOverLifetimeModule;
    }
    public set useColorOverLifetimeModule(value: boolean) {
        if (this._useColorOverLifetimeModule === value) {
            return;
        }
        this._useColorOverLifetimeModule = value;
        this._updateUseColorOverLifetimeModuleProperty();
    }

    @property({ type: CCBoolean, displayName: DEV && '採用原圖顏色', tooltip: DEV && '是否採用原圖顏色' })
    public get useOriginColor(): boolean {
        return this._useOriginColor;
    }
    public set useOriginColor(value: boolean) {
        if (this._useOriginColor === value) {
            return;
        }
        this._useOriginColor = value;
        Tint.updateUseOriginColorProperty(this.targetPass, value);
    }

    @property({ type: CCBoolean, tooltip: DEV && '是否採用預乘 Alpha' })
    public get premultipliedAlpha(): boolean {
        return this._premultipliedAlpha;
    }
    public set premultipliedAlpha(value: boolean) {
        if (this._premultipliedAlpha === value) {
            return;
        }
        this._premultipliedAlpha = value;
        Tint.updatePremultipliedAlphaProperty(this.targetPass, value);
    }

    @property({ slide: true, range: [0, 1, 0.01], displayName: DEV && '灰階偏移值', tooltip: DEV && '灰階偏移值(0~1)' })
    public get grayValueOffset(): number {
        return this._grayValueOffset;
    }
    public set grayValueOffset(value: number) {
        if (this._grayValueOffset === value) {
            return;
        }
        this._grayValueOffset = value;
        Tint.updateGrayValueOffsetProperty(this.targetPass, value);
    }

    @property({ type: Texture2D, displayName: DEV && '主圖', tooltip: DEV && 'Particle mainTexture' })
    public get mainTexture(): Texture2D {
        return this._mainTexture;
    }
    public set mainTexture(value: Texture2D) {
        if (this._mainTexture === value) {
            return;
        }
        this._mainTexture = value;
        this._updateMainTextureProperty();
    }

    @property({ type: Gradient, displayName: DEV && 'Tint 顏色設定' })
    public get tintGradient(): Gradient {
        return this._tintGradient;
    }
    public set tintGradient(value: Gradient) {
        if (this._tintGradient === value) {
            return;
        }
        this._tintGradient = value;
        this._updateGradientProperty();
    }

    @property({ type: CCBoolean, displayName: DEV && '啟用範圍採樣', tooltip: DEV && '勾選即可設定顏色採樣範圍' })
    public get useSampleBound(): boolean {
        return this._useSampleBound;
    }
    public set useSampleBound(value: boolean) {
        if (this._useSampleBound === value) {
            return;
        }
        this._useSampleBound = value;
        this._updateGradientProperty();
    }

    @property({
        slide: true,
        range: [0, 1, 0.001],
        displayName: DEV && '採樣左邊界',
        tooltip: DEV && '左邊界大於右邊界時會以左邊界顏色為主',
        visible(this: TintParticle) {
            return this.useSampleBound;
        },
    })
    public get sampleLeftBound(): number {
        return this._sampleLeftBound;
    }
    public set sampleLeftBound(value: number) {
        if (this._sampleLeftBound === value) {
            return;
        }
        this._sampleLeftBound = value;
        this._updateGradientProperty();
    }

    @property({
        slide: true,
        range: [0, 1, 0.001],
        displayName: DEV && '採樣右邊界',
        tooltip: DEV && '左邊界大於右邊界時會以左邊界顏色為主',
        visible(this: TintParticle) {
            return this.useSampleBound;
        },
    })
    public get sampleRightBound(): number {
        return this._sampleRightBound;
    }
    public set sampleRightBound(value: number) {
        if (this._sampleRightBound === value) {
            return;
        }
        this._sampleRightBound = value;
        this._updateGradientProperty();
    }

    @property({ type: Sprite, displayName: DEV && '漸層預覽測試', tooltip: DEV && '圖片大小固定為0或100，用來預覽取樣漸層顏色' })
    public get testSampleColor(): Sprite {
        return this._testSampleColor;
    }
    public set testSampleColor(value: Sprite) {
        if (this._testSampleColor === value) {
            return;
        }
        if (value === null) {
            this._testSampleColor.node.getComponent(UITransform).setContentSize(0, 0);
        }
        this._testSampleColor = value;
        Tint.showSampleColor(this.testSampleColor, this._texture);
    }

    protected syncProperty() {
        this._updateGradientProperty();
        this._updateMainTextureProperty();
        this._updateUseColorOverLifetimeModuleProperty();
        Tint.updateUseOriginColorProperty(this.targetPass, this.useOriginColor);
        Tint.updatePremultipliedAlphaProperty(this.targetPass, this.premultipliedAlpha);
        Tint.updateGrayValueOffsetProperty(this.targetPass, this.grayValueOffset);
    }

    // 將Texture2D傳遞給Shader後更改Material。
    private _updateGradientProperty() {
        if (!this.targetPass) {
            return;
        }
        Tint.assignGradientTexture(this._texture, this.tintGradient, this.textureWidth, this.useSampleBound, this.sampleLeftBound, this.sampleRightBound);
        Tint.showSampleColor(this.testSampleColor, this._texture);
        Tint.updateGradientProperty(this.targetPass, this._texture);
    }

    // 將Texture2D傳遞給Shader後更改Material。
    private _updateMainTextureProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        pass.bindTexture(pass.getBinding('mainTexture'), this.mainTexture.getGFXTexture());
        pass.bindSampler(pass.getBinding('mainTexture'), this.mainTexture.getGFXSampler());
    }

    // 選取是否要採用 Paricle 上的 ColorOverLifetimeModule。
    private _updateUseColorOverLifetimeModuleProperty() {
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        const { color } = this.getComponent(ParticleSystem).startColor;
        pass.setUniform(pass.getHandle('particleColor'), v4(color.r / 255, color.g / 255, color.b / 255, color.a / 255));
        pass.setUniform(pass.getHandle('useColorOverLifetimeModule'), this.useColorOverLifetimeModule ? 1 : 0);
    }
}
